/**
 * La connexion au live perdue, dite sur la vue Activité (Wajih, 23/10, R71 de `ports/TRANSITION.md`) : la
 * visio le dit sous son voile (« Reconnexion… », puis « Réessayer »), mais l'élève sur son activité ne la
 * voit pas — l'ancien espace l'écrivait sur sa mini-visio, posée par-dessus l'activité. Une règle pure ;
 * la page la pose en barre sous l'en-tête, à côté de « Connexion faible ».
 *
 *  - **Seulement une reprise** (`reconnecting`) : l'entrée dans le live (`connecting`) se dit sur la visio,
 *    où l'élève arrive.
 *  - **Seulement hors de la visio** : la vue Activité au téléphone ; au grand écran, l'activité quand la
 *    visio n'est plus à l'écran (le Concentré, visio refermée).
 *  - **Après `LIVE_LOST_NOTICE_DELAY_MS`** : une micro-coupure se reprend sans que l'élève le lise.
 *  - **« Réessayer » quand la reprise bloque** : la même condition que sur la visio (l'écran offre la
 *    relance au-delà de 20 s de reprise sans issue, `STUDENT_RECONNECT_CEILING_MS`).
 *  - **Le live injoignable** (`unreachable` : la salle a refusé le jeton redemandé) se dit tout de suite :
 *    rien ne se relance plus avant « Réessayer ».
 */
import { type UILiveConnection } from "../../../ports/primary/ui/types/studentLive"

/**
 * Cinq secondes : les trois premiers essais de reprise du SDK partent dans les 2,5 premières secondes (0,
 * 0,3 puis 1,2 s d'attente, gigue comprise, `engine/live/room/roomOptions`) et reprennent une micro-coupure ;
 * le quatrième ne part qu'entre 4,2 et 6,2 s. Une coupure qui passe ce cap dure : l'élève le lit. Plus tôt, la
 * barre clignoterait à chaque saute de réseau ; plus tard, il resterait sur une activité sans savoir que son
 * masteur ne l'entend plus.
 */
export const LIVE_LOST_NOTICE_DELAY_MS = 5000

/** La barre est posée ; elle offre « Réessayer » quand l'écran offre la relance. */
export function liveLostNoticeShown(input: {
    connection: UILiveConnection | null
    /** La reprise dure depuis `LIVE_LOST_NOTICE_DELAY_MS` au moins. */
    lasting: boolean
    /** La reprise bloque : l'écran offre la relance (« Réessayer » sur la visio). */
    canRetry: boolean
    /** La visio est à l'écran : elle le dit elle-même, sous son voile. */
    stageOnScreen: boolean
}): boolean {
    const { connection, lasting, canRetry, stageOnScreen } = input
    if (stageOnScreen) return false
    return connection === "unreachable" || (connection === "reconnecting" && (lasting || canRetry))
}
